import { Autocomplete, Box, Checkbox, FormControl, FormHelperText, Stack, TextField, Typography, useMediaQuery } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { FunctionComponent, SetStateAction } from 'react'
import gaiaTheme from '../../../../root-components/theme';
import { TooltipButton } from '../../miscellaneous-components/tooltip-button';
import FormEditButtons from './form-edit-buttons';
import { CheckboxVirtualizedListbox, VirtualizedListbox, renderVirtualizedGroup } from '../virtualized-listbox';

export const FormAutoCompleteInputDisplay: FunctionComponent<{
  isEditPage: boolean,
  allowEdit?: boolean,
  editField?: string | undefined,
  setEditField?: React.Dispatch<SetStateAction<any>> | undefined,
  hideEditIcon?: boolean,
  disabled: boolean,
  isLastField?: boolean,
  size?: 'small' | 'medium',
  title: string,
  name: string,
  value: any,
  touched?: any,
  errors?: any,
  onChange: any,
  cancelEditHandler?: () => void,
  options: any,
  optionLabelKey: string,
  optionText?: string,
  optionImage?: any,
  customErrorMessage?: string,
  bottomBorder?: boolean,
  topBorder?: boolean,
  sideBorders?: boolean,
  isTopRow?: boolean,
  isBottomRow?: boolean,
  freeSolo?: boolean,
  onInputChange?: (event: any, newInputValue: string) => void,
  forceOutlined?: boolean,
  multiple?: boolean,
  groupBy?: string | ((option: any) => string),
  getOptionLabel?: (option: any) => string,
  tooltip?: string,
  /** Mount only the options in view. For long lists such as suppliers. */
  virtualized?: boolean,
}> = ({
  isEditPage,
  allowEdit = true,
  editField,
  setEditField,
  hideEditIcon = false,
  disabled,
  isLastField = false,
  size = 'small',
  title,
  name,
  value,
  touched,
  errors,
  onChange,
  cancelEditHandler,
  options,
  optionLabelKey,
  optionText,
  optionImage,
  customErrorMessage,
  bottomBorder = false,
  topBorder = true,
  sideBorders = true,
  isTopRow = false,
  isBottomRow = false,
  freeSolo = false,
  onInputChange,
  forceOutlined = false,
  multiple = false,
  groupBy,
  getOptionLabel: customGetOptionLabel,
  tooltip,
  virtualized = false,
}) => {

    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));

    const toggleEditField = (fieldName: string) => {
      if (setEditField) {
        setEditField(fieldName)
      }
    }

    return (
      <FormControl fullWidth variant='outlined'>
        <Stack sx={{
          borderWidth: '1px',
          borderBottomStyle: bottomBorder || isBottomRow ? 'solid' : 'none',
          borderColor: 'lightGrey.main',
          borderLeftStyle: sideBorders ? 'solid' : 'none',
          borderRightStyle: sideBorders ? 'solid' : 'none',
          borderTopStyle: topBorder || isTopRow ? 'solid' : 'none',
          pt: 1, pb: 1, pl: 3,
          borderTopLeftRadius: isTopRow ? '10px' : 0,
          borderTopRightRadius: isTopRow ? '10px' : 0,
          borderBottomLeftRadius: isBottomRow ? '10px' : 0,
          borderBottomRightRadius: isBottomRow ? '10px' : 0
        }}>
          <Stack position='relative' alignItems={isMobileMode ? 'flex-start' : 'center'} flexDirection={isMobileMode ? 'column' : 'row'} justifyContent='start'>
            <Stack sx={{ width: isMobileMode ? '100%' : '40%', minWidth: '100px', color: 'text.primary', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <Typography variant='subtitle1'> {title} </Typography>
              {tooltip && !isMobileMode && <TooltipButton
                text={tooltip}
                noPadding={true}
                placement='bottom' />}
            </Stack>
            <Stack position='relative' flexDirection='column' sx={{ width: '100%' }}>
              <Stack position='relative' flexDirection='row' justifyContent='start' alignItems='center'>
                <Autocomplete
                  id={name}
                  disabled={disabled}
                  size={size}
                  fullWidth
                  options={options}
                  disableCloseOnSelect={multiple}
                  multiple={multiple}
                  freeSolo={freeSolo}
                  groupBy={groupBy ? (typeof groupBy === 'function' ? groupBy : (option: any) => option[groupBy]) : undefined}
                  ListboxComponent={virtualized ? (multiple ? CheckboxVirtualizedListbox : VirtualizedListbox) : undefined}
                  renderGroup={virtualized && groupBy ? renderVirtualizedGroup : undefined}
                  sx={{
                    mt: 1, mb: 1, mr: 15, ml: 1,
                    ...((editField !== name || isEditPage) && !forceOutlined && {
                      '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    }),
                    ...((editField === name || !isEditPage || forceOutlined) && {
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'text.secondary' }
                    }),
                    ...(!isEditPage && {
                      mt: 1, mb: 1, mr: 2, ml: 2,
                    }),
                    color: 'text.secondary',
                  }}
                  autoHighlight
                  getOptionLabel={customGetOptionLabel || ((option) => {
                    if (typeof option === 'string') return option
                    return `${option[optionLabelKey]}`
                  })}
                  renderOption={(props, option, state: any) => {
                    const { key, ...restProps } = props as { key: React.Key } & React.HTMLAttributes<HTMLLIElement>;
                    return (
                      <Box
                        component="li"
                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                        key={key}
                        {...restProps}
                      >
                        {multiple && (
                          <Checkbox
                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                            style={{ marginRight: 8 }}
                            checked={state?.selected}
                          />
                        )}
                        {typeof option === 'string' ? (
                          option
                        ) : customGetOptionLabel ? (
                          customGetOptionLabel(option)
                        ) : optionImage ? (
                          <>
                            <img
                              loading="lazy"
                              width="20"
                              src={option[optionImage]}
                              alt="country flag" />
                            {option[optionLabelKey]}
                          </>
                        ) : optionText ? (<>{option[optionText]}: {option[optionLabelKey]} </>) :
                          option[optionLabelKey]
                        }
                      </Box>
                    )
                  }}
                  value={value}
                  onChange={onChange}
                  onInputChange={onInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={touched && Boolean(errors)}
                    // helperText={touched && errors}
                    />
                  )}
                />
                {allowEdit &&
                  <FormEditButtons
                    isEditPage={isEditPage}
                    editField={editField}
                    hideEditIcon={hideEditIcon}
                    name={name}
                    toggleEditField={toggleEditField}
                    cancelEditHandler={cancelEditHandler}
                  />}
              </Stack>
              {touched && errors &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errors ? <div>{errors as string}</div> : null}
                </FormHelperText>
              }
              {customErrorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{customErrorMessage}
                </FormHelperText>
              }
            </Stack>
          </Stack>
        </Stack>
      </FormControl>
    )
  }